import { apiCall, ApiResult } from './core';

export const communicationAPI = {
  getNotifications: async (_userId?: string): Promise<ApiResult> => {
    try {
      const data = await apiCall('/communications/notifications');
      return { success: true, data: data.notifications || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  markNotificationRead: async (notificationId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/communications/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  }
};

// Unified Notification System API
export const notificationAPI = {
  // Get notifications with optional filters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic notification data
  getNotifications: async (filters: { unreadOnly?: boolean; category?: string; limit?: number; offset?: number } = {}): Promise<{ success: boolean; data?: any[]; total?: number; unreadCount?: number; error?: string }> => {
    try {
      const params = new URLSearchParams();
      if (filters.unreadOnly) params.set('unread_only', 'true');
      if (filters.category) params.set('category', filters.category);
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));

      const queryString = params.toString();
      const data = await apiCall(`/notifications${queryString ? '?' + queryString : ''}`);
      return {
        success: true,
        data: data.notifications || [],
        total: data.total || 0,
        unreadCount: data.unread_count || 0
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get unread count only
  getUnreadCount: async (): Promise<{ success: boolean; unreadCount?: number; error?: string }> => {
    try {
      const data = await apiCall('/notifications/unread-count');
      return { success: true, unreadCount: data.unread_count || 0 };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Mark single notification as read
  markAsRead: async (notificationId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<{ success: boolean; markedCount?: number; error?: string }> => {
    try {
      const data = await apiCall('/notifications/read-all', {
        method: 'PUT'
      });
      return { success: true, markedCount: data.marked_count || 0 };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Delete (soft delete) a notification
  deleteNotification: async (notificationId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Mark single notification as unread
  markAsUnread: async (notificationId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/notifications/${notificationId}/unread`, {
        method: 'PUT'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Bulk mark notifications as read
  bulkMarkAsRead: async (notificationIds: string[]): Promise<{ success: boolean; markedCount?: number; error?: string }> => {
    try {
      const data = await apiCall('/notifications/bulk/read', {
        method: 'PUT',
        body: JSON.stringify({ notification_ids: notificationIds })
      });
      return { success: true, markedCount: data.marked_count || 0 };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Bulk mark notifications as unread
  bulkMarkAsUnread: async (notificationIds: string[]): Promise<{ success: boolean; markedCount?: number; error?: string }> => {
    try {
      const data = await apiCall('/notifications/bulk/unread', {
        method: 'PUT',
        body: JSON.stringify({ notification_ids: notificationIds })
      });
      return { success: true, markedCount: data.marked_count || 0 };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Bulk delete notifications
  bulkDelete: async (notificationIds: string[]): Promise<{ success: boolean; deletedCount?: number; error?: string }> => {
    try {
      const data = await apiCall('/notifications/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ notification_ids: notificationIds })
      });
      return { success: true, deletedCount: data.deleted_count || 0 };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get user preferences
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic preference data
  getPreferences: async (): Promise<{ success: boolean; preferences?: any[]; defaultTypes?: any[]; error?: string }> => {
    try {
      const data = await apiCall('/notifications/preferences');
      return {
        success: true,
        preferences: data.preferences || [],
        defaultTypes: data.default_types || []
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Update user preferences
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic preference data
  updatePreferences: async (preferences: any[]): Promise<ApiResult> => {
    try {
      const data = await apiCall('/notifications/preferences', {
        method: 'PUT',
        body: JSON.stringify({ preferences })
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get user subscriptions
  getSubscriptions: async (subscriptionType: string | null = null): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (subscriptionType) params.set('subscription_type', subscriptionType);
      const queryString = params.toString();
      const data = await apiCall(`/notifications/subscriptions${queryString ? '?' + queryString : ''}`);
      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Subscribe to a resource
  subscribe: async (subscriptionData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall('/notifications/subscriptions', {
        method: 'POST',
        body: JSON.stringify(subscriptionData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Unsubscribe from a resource
  unsubscribe: async (subscriptionType: string, subscriptionId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/notifications/subscriptions/${subscriptionType}/${subscriptionId}`, {
        method: 'DELETE'
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

};

// Notification Catalog API (admin configuration)
// All methods accept an optional clientId parameter for partner/AWS users
export const notificationCatalogAPI = {
  // Get full catalog with client configurations merged
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic catalog data
  getCatalog: async (category: string | null = null, clientId: string | null = null): Promise<{ success: boolean; items?: any[]; total?: number; categories?: any[]; error?: string }> => {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (clientId) params.set('client_id', clientId);
      const queryString = params.toString();
      const data = await apiCall(`/notification-catalog/catalog${queryString ? '?' + queryString : ''}`);
      return {
        success: true,
        items: data.items || [],
        total: data.total || 0,
        categories: data.categories || [],
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get all notification definitions
  getDefinitions: async (category: string | null = null): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      const queryString = params.toString();
      const data = await apiCall(`/notification-catalog/definitions${queryString ? '?' + queryString : ''}`);
      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get client-specific configurations
  getClientConfigs: async (clientId: string | null = null): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      const queryString = params.toString();
      const data = await apiCall(`/notification-catalog/client-config${queryString ? '?' + queryString : ''}`);
      return { success: true, data: data || [] };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get single client config
  getClientConfig: async (notificationCode: string, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      const queryString = params.toString();
      const data = await apiCall(`/notification-catalog/client-config/${encodeURIComponent(notificationCode)}${queryString ? '?' + queryString : ''}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Update client configuration for a notification
  updateClientConfig: async (notificationCode: string, config: Record<string, any>, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      const queryString = params.toString();
      const data = await apiCall(`/notification-catalog/client-config/${encodeURIComponent(notificationCode)}${queryString ? '?' + queryString : ''}`, {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Reset client configuration to defaults
  resetClientConfig: async (notificationCode: string, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      const queryString = params.toString();
      await apiCall(`/notification-catalog/client-config/${encodeURIComponent(notificationCode)}${queryString ? '?' + queryString : ''}`, {
        method: 'DELETE',
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Get queue statistics
  getQueueStats: async (clientId: string | null = null): Promise<{ success: boolean; pending?: number; processing?: number; sentToday?: number; failedToday?: number; skippedToday?: number; error?: string }> => {
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      const queryString = params.toString();
      const data = await apiCall(`/notification-catalog/queue/stats${queryString ? '?' + queryString : ''}`);
      return {
        success: true,
        pending: data.pending || 0,
        processing: data.processing || 0,
        sentToday: data.sent_today || 0,
        failedToday: data.failed_today || 0,
        skippedToday: data.skipped_today || 0,
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },
};

// Celery Task Executions (ops dashboard)
export const taskExecutionsAPI = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic execution data
  listExecutions: async (filters: { limit?: number; offset?: number; status?: string; taskName?: string; taskNamePrefix?: string; since?: string; sortBy?: string; sortOrder?: string } = {}): Promise<{ success: boolean; executions?: any[]; total?: number; limit?: number; offset?: number; error?: string }> => {
    try {
      const params = new URLSearchParams();
      if (filters.limit != null) params.set('limit', String(filters.limit));
      if (filters.offset != null) params.set('offset', String(filters.offset));
      if (filters.status) params.set('status', filters.status);
      if (filters.taskName) params.set('task_name', filters.taskName);
      if (filters.taskNamePrefix) params.set('task_name_prefix', filters.taskNamePrefix);
      if (filters.since) params.set('since', filters.since);
      if (filters.sortBy) params.set('sort_by', filters.sortBy);
      if (filters.sortOrder) params.set('sort_order', filters.sortOrder);

      const queryString = params.toString();
      const data = await apiCall(`/tasks/executions${queryString ? '?' + queryString : ''}`);
      return {
        success: true,
        executions: data.executions || [],
        total: data.total || 0,
        limit: data.limit || (filters.limit ?? 50),
        offset: data.offset || (filters.offset ?? 0),
      };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getSummary: async (filters: { windowHours?: number; status?: string; taskName?: string; taskNamePrefix?: string } = {}): Promise<ApiResult> => {
    try {
      const params = new URLSearchParams();
      if (filters.windowHours != null) params.set('window_hours', String(filters.windowHours));
      if (filters.status) params.set('status', filters.status);
      if (filters.taskName) params.set('task_name', filters.taskName);
      if (filters.taskNamePrefix) params.set('task_name_prefix', filters.taskNamePrefix);
      const queryString = params.toString();
      const data = await apiCall(`/tasks/executions/summary${queryString ? '?' + queryString : ''}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getExecution: async (executionId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/tasks/executions/${executionId}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getByTaskId: async (taskId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/tasks/executions/by-task-id/${encodeURIComponent(taskId)}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  deleteExecution: async (executionId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/tasks/executions/${executionId}`, { method: 'DELETE' });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  bulkDelete: async (ids: string[]): Promise<ApiResult> => {
    try {
      const data = await apiCall('/tasks/executions/bulk', {
        method: 'DELETE',
        body: JSON.stringify({ ids }),
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },
};
